/**
 * Module NEUTRE « column-count » — KEEP IN SYNC avec
 * inventozen-frontpublic/src/cms-puck/column-count.ts
 *
 * Nombre de colonnes pour les blocs de contenu (galerie, sélection, listing…).
 * Plafond CSS commun des blocs de contenu (grilles `repeat(N, 1fr)`).
 */

import type { CSSProperties } from "react";

/** Plafond du nombre de colonnes (slider éditeur + rendu). */
export const MAX_CONTENT_COLUMNS = 7;

export const CMS_CONTENT_COLUMNS_CLASS = "cms-content-columns";

export function clampColumnCount(
  value: unknown,
  opts: { min?: number; max?: number; fallback?: number } = {},
): number {
  const min = opts.min ?? 1;
  const max = opts.max ?? MAX_CONTENT_COLUMNS;
  const fallback = opts.fallback ?? min;
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

/** Style inline pour grilles à colonnes égales (complète la classe CSS responsive). */
export function contentColumnsGridStyle(columns: number): CSSProperties {
  const n = clampColumnCount(columns);
  return { "--cms-cols": String(n) } as CSSProperties;
}

/** className + style pour une grille de contenu responsive (1 col. mobile). */
export function contentColumnsGridAttrs(
  columns: unknown,
  opts?: { min?: number; max?: number; fallback?: number },
): { className: string; style: CSSProperties } {
  const n = clampColumnCount(columns, opts);
  return {
    className: `grid gap-4 ${CMS_CONTENT_COLUMNS_CLASS}`,
    style: contentColumnsGridStyle(n),
  };
}

/**
 * Grille de l'ancienne page Actualités : 1 colonne en mobile (< 768), 2 en
 * tablette (jusqu'à 1023), puis `columns` (jusqu'à 3 de base) — cf. `NewsPage`
 * (`grid-cols-1 md:grid-cols-2 lg:grid-cols-3`). L'écart vient du bloc.
 */
export function newsColumnsGridAttrs(columns: unknown): {
  className: string;
  style: CSSProperties;
} {
  const n = clampColumnCount(columns);
  return {
    className: "cms-news-columns",
    style: {
      "--cms-cols": String(n),
      "--cms-cols-tablet": String(Math.min(n, 2)),
    } as CSSProperties,
  };
}

/** Largeur % d'un item en mode grille flex (sélection d'œuvres). */
export function selectionColumnWidthPercent(columns: number): number {
  const n = clampColumnCount(columns);
  if (n === 2) return 45;
  if (n === 3) return 30;
  if (n === 4) return 22;
  return Math.max(12, Math.floor(88 / n));
}

/**
 * Cellule d'une sélection en grille : pleine largeur en mobile (`< md`), puis
 * `widthPct` — comme l'ancien GridDisplay (`getMaxWidthPercent`, 100 % en mobile).
 */
export function selectionCellLayout(widthPct: number): {
  className: string;
  style: Record<string, string>;
} {
  return {
    className: "w-full md:w-[var(--selection-cell-w)]",
    style: { "--selection-cell-w": `${widthPct}%` },
  };
}
